import React, { useEffect, useMemo, useState } from 'react';
import { MatrixCell, MatrixProfile, cellStyle } from './matrixUtils';

// 矩阵识别中心的共用组件 (列表 + 详情); 类型/常量/纯函数在 matrixUtils.ts.
// 网格叠加的算法移植自 studio-admin-console dash/MatrixDetailDrawer.vue, 口径与之一致.

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
    done: { text: '完成', cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    failed: { text: '失败', cls: 'bg-rose-50 text-rose-600 border-rose-200' },
    processing: { text: '处理中', cls: 'bg-amber-50 text-amber-600 border-amber-200' },
};

export function MatrixStatusBadge({ status }: { status: string }) {
    const v = STATUS_LABEL[status] || { text: status || '—', cls: 'bg-slate-50 text-slate-400 border-slate-200' };
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${v.cls}`}>
            {v.text}
        </span>
    );
}


type Rect = { x: number; y: number; w: number; h: number };

// bbox 单位判定: 全 ≤100 视为百分比, 有 >100 视为像素 (生产: 单元格像素、档案百分比, 各自判定).
function allPercent(list: { bbox?: number[] }[]): boolean {
    return list.every((a) => !Array.isArray(a.bbox) || a.bbox.every((v) => typeof v !== 'number' || v <= 100));
}

const CELL_INSET = 0.12;
const PROFILE_INSET = 0.05;

function rectFromBbox(bbox: number[] | null | undefined, pct: boolean, size: { w: number; h: number }, inset: number): Rect | null {
    if (!Array.isArray(bbox) || bbox.length < 4 || !bbox.every((n) => typeof n === 'number')) return null;
    let [x1, y1, x2, y2] = bbox;
    if (pct) { x1 = x1 / 100 * size.w; y1 = y1 / 100 * size.h; x2 = x2 / 100 * size.w; y2 = y2 / 100 * size.h; }
    const w = Math.max(x2 - x1, 0);
    const h = Math.max(y2 - y1, 0);
    if (w <= 0 || h <= 0) return null;
    const ix = w * inset;
    const iy = h * inset;
    return { x: x1 + ix, y: y1 + iy, w: w - ix * 2, h: h - iy * 2 };
}

// 没自带 bbox 的格子: 用档案的 bbox + row/col 推网格 (原点 + 步长).
function gridLayout(profiles: MatrixProfile[]) {
    const valid = profiles.filter((p) => Array.isArray(p.bbox) && p.bbox.length >= 4 && p.row != null && p.col != null);
    if (!valid.length) return null;
    const r0 = valid[0];
    const b0 = r0.bbox as number[];
    const cellW = b0[2] - b0[0];
    const cellH = b0[3] - b0[1];
    let stepX = cellW;
    let stepY = cellH;
    const dc = valid.find((p) => p.col !== r0.col);
    if (dc) stepX = Math.abs((dc.bbox as number[])[0] - b0[0]) / Math.abs((dc.col as number) - (r0.col as number));
    const dr = valid.find((p) => p.row !== r0.row);
    if (dr) stepY = Math.abs((dr.bbox as number[])[1] - b0[1]) / Math.abs((dr.row as number) - (r0.row as number));
    return { originX: b0[0] - (r0.col as number) * stepX, originY: b0[1] - (r0.row as number) * stepY, stepX, stepY, cellW, cellH };
}

function profileColor(tag: string): string {
    const t = tag.toLowerCase();
    if (t.includes('best')) return '#7c3aed';
    if (t.includes('fast')) return '#0284c7';
    if (t.includes('deep')) return '#c026d3';
    return '#4f46e5';
}

/**
 * MatrixGridOverlay 原图上叠每格框 (按评级着色, 中间写 Score) + 参数档案标记环.
 * SVG viewBox 用原图自然尺寸, 与渲染尺寸解耦.
 */
export function MatrixGridOverlay({
    src,
    cells,
    profiles,
    onZoom,
}: {
    src: string;
    cells: MatrixCell[];
    profiles: MatrixProfile[];
    onZoom?: (src: string) => void;
}) {
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    const [broken, setBroken] = useState(false);
    useEffect(() => {
        setSize(null);
        setBroken(false);
        if (!src) return undefined;
        let alive = true;
        const img = new Image();
        img.onload = () => { if (alive && img.naturalWidth > 0) setSize({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.onerror = () => { if (alive) setBroken(true); };
        img.src = src;
        return () => { alive = false; };
    }, [src]);

    const marks = useMemo(() => {
        if (!size) return { cells: [] as { c: MatrixCell; r: Rect }[], profiles: [] as { p: MatrixProfile; r: Rect; text: string; color: string }[] };
        const cellPct = allPercent(cells);
        const profPct = allPercent(profiles);
        const g = gridLayout(profiles);
        const gridBbox = (row: number, col: number) => {
            if (!g) return null;
            const x1 = g.originX + col * g.stepX;
            const y1 = g.originY + row * g.stepY;
            return [x1, y1, x1 + g.cellW, y1 + g.cellH];
        };
        const cellMarks = cells.map((c) => ({
            c,
            r: rectFromBbox(c.bbox, cellPct, size, CELL_INSET) ?? rectFromBbox(gridBbox(c.row, c.col), profPct, size, CELL_INSET),
        })).filter((m): m is { c: MatrixCell; r: Rect } => !!m.r);
        const profMarks = profiles.map((p) => {
            const r = rectFromBbox(p.bbox, profPct, size, PROFILE_INSET)
                ?? (p.row != null && p.col != null ? rectFromBbox(gridBbox(p.row, p.col), profPct, size, PROFILE_INSET) : null);
            const text = String(p.tag ?? p.name ?? '标记');
            return r ? { p, r, text, color: profileColor(text) } : null;
        }).filter((m): m is { p: MatrixProfile; r: Rect; text: string; color: string } => !!m);
        return { cells: cellMarks, profiles: profMarks };
    }, [size, cells, profiles]);

    // 图床 404 / 测试库造数据的地址: 明写「图失效」, 不留浏览器裂图.
    if (broken) {
        return (
            <div className="w-full h-40 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 flex items-center justify-center"
                title={src}>矩阵图打不开（地址失效或格式浏览器不支持）</div>
        );
    }
    return (
        <div className="relative inline-block max-w-full">
            <img src={src} alt="矩阵图" className="block max-h-[560px] max-w-full rounded-md ring-1 ring-slate-100 cursor-zoom-in"
                onClick={() => onZoom?.(src)} />
            {size && (marks.cells.length > 0 || marks.profiles.length > 0) && (
                <svg viewBox={`0 0 ${size.w} ${size.h}`} preserveAspectRatio="xMidYMid meet"
                    className="absolute inset-0 h-full w-full pointer-events-none">
                    {marks.cells.map(({ c, r }) => {
                        const st = cellStyle(c.type);
                        return (
                            <g key={c.id ?? `${c.row}-${c.col}`} className="pointer-events-auto">
                                <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={st.fill} stroke={st.stroke} strokeWidth={2} rx={4}>
                                    <title>{`R${c.row}/C${c.col} · 功率 ${c.power ?? '—'} 速度 ${c.speed ?? '—'} Score ${c.score ?? '—'}\n${c.description ?? ''}`}</title>
                                </rect>
                                <text x={r.x + r.w / 2} y={r.y + r.h / 2} textAnchor="middle" dominantBaseline="central"
                                    fill={st.text} fontSize={Math.max(r.w / 4, 10)} fontWeight="bold"
                                    fontFamily="ui-monospace, SFMono-Regular, monospace">{c.score}</text>
                            </g>
                        );
                    })}
                    {marks.profiles.map(({ p, r, text, color }, i) => {
                        const fs = Math.max(r.w / 7, 11);
                        const bh = fs * 1.5;
                        const bw = text.length * fs * 0.62 + fs * 0.7;
                        const by = Math.max(r.y - bh - 2, 0);
                        return (
                            <g key={p.key ?? `p${i}`}>
                                <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="none" stroke={color} strokeWidth={4} rx={6}>
                                    <title>{text}</title>
                                </rect>
                                <rect x={r.x} y={by} width={bw} height={bh} fill={color} rx={4} />
                                <text x={r.x + bw / 2} y={by + bh / 2} textAnchor="middle" dominantBaseline="central"
                                    fill="#fff" fontSize={fs} fontWeight="bold">{text}</text>
                            </g>
                        );
                    })}
                </svg>
            )}
        </div>
    );
}
